import { useMemo, useState } from "react";
import { Card, CardContent } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import RelatorioGeralAcao from "./RelatorioGeralAcao";
import RelatorioConsultaParticipantes from "./RelatorioConsultaParticipantes";
import RelatorioGerencialCensoSuas from "./RelatorioGerencialCensoSuas";
import RelatorioInscritosPorMunicipio from "./RelatorioInscritosPorMunicipio";
import RelatorioMigracaoEducaSuas from "./RelatorioMigracaoEducaSuas";
import RelatorioTotalQualificados from "./RelatorioTotalQualificados";
import RelatorioTotalParticipacoes from "./RelatorioTotalParticipacoes";
import styles from "../Relatorios.module.css";

type ReportId =
  | "geral-acao"
  | "total-participacoes"
  | "consulta-participantes"
  | "inscritos-municipio"
  | "total-qualificados"
  | "migracao-educasuas"
  | "relatorio-gerencial";

type ReportOption = {
  id: ReportId;
  order: string;
  title: string;
  description: string;
  icon: JSX.Element;
};

const reportOptions: ReportOption[] = [
  {
    id: "geral-acao",
    order: "1.",
    title: "Geral da Ação",
    description: "Lista completa de inscritos de uma ou mais ações/turmas, com desempenho.",
    icon: <DescriptionOutlinedIcon />,
  },
  {
    id: "total-participacoes",
    order: "2.",
    title: "Total de Participações",
    description: "Soma de todas as aprovações por município.",
    icon: <BarChartOutlinedIcon />,
  },
  {
    id: "consulta-participantes",
    order: "3.",
    title: "Consulta de Participantes",
    description: "Histórico completo de um CPF específico ou de um município.",
    icon: <ManageSearchOutlinedIcon />,
  },
  {
    id: "inscritos-municipio",
    order: "4.",
    title: "Inscritos por Município",
    description: "Preenchimento das vagas disponibilizadas por território.",
    icon: <LocationCityOutlinedIcon />,
  },
  {
    id: "total-qualificados",
    order: "5.",
    title: "Total de Qualificados",
    description: "Pessoas únicas qualificadas (CPFs únicos).",
    icon: <SchoolOutlinedIcon />,
  },
  {
    id: "migracao-educasuas",
    order: "6.",
    title: "Migração EducaSuas/MG (Moodle CSV)",
    description: "Exportação de usuários para o padrão de importação do Moodle.",
    icon: <CloudUploadOutlinedIcon />,
  },
  {
    id: "relatorio-gerencial",
    order: "7.",
    title: "Relatório Gerencial",
    description: "Censo SUAS (Educação Permanente).",
    icon: <DashboardOutlinedIcon />,
  },
];

const RelatoriosManager = () => {
  const [selectedReportId, setSelectedReportId] = useState<ReportId>("geral-acao");

  const selectedReport = useMemo(
    () => reportOptions.find((item) => item.id === selectedReportId) ?? reportOptions[0],
    [selectedReportId],
  );

  return (
    <Card className={styles.reportPageCard}>
      <CardContent className={styles.reportPageContent}>
        <div className={styles.reportPageHeader}>
          <div>
            <div className={styles.title}>Relatórios</div>
            <div className={styles.pageSubtitle}>
              Selecione o relatório desejado e defina os filtros para gerar os dados.
            </div>
          </div>

          <div className={styles.noticePanel}>
            <div className={styles.noticeIconWrap}>
              <InfoOutlinedIcon className={styles.noticeIcon} />
            </div>
            <div>
              <div className={styles.noticeTitle}>Atenção</div>
              <div className={styles.noticeText}>
                Os relatórios exportam os dados conforme os filtros selecionados. Verifique sempre
                o período e os demais critérios antes da geração.
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reportShell}>
          <aside className={styles.reportSidebar}>
            <div className={styles.reportSectionTitle}>1. Escolha o relatório</div>
            <div className={styles.reportSectionText}>
              Selecione o tipo de relatório que deseja gerar.
            </div>

            <div className={styles.reportOptionList}>
              {reportOptions.map((item) => {
                const active = item.id === selectedReportId;

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`${styles.reportOptionCard} ${active ? styles.reportOptionCardActive : ""}`}
                    onClick={() => setSelectedReportId(item.id)}
                  >
                    <div className={styles.reportOptionIcon}>{item.icon}</div>
                    <div className={styles.reportOptionContent}>
                      <div className={styles.reportOptionTitle}>
                        {item.order} {item.title}
                      </div>
                      <div className={styles.reportOptionDescription}>{item.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className={styles.reportContentPanel}>
            <div className={styles.reportSectionTitle}>Filtros do relatório</div>
            {selectedReportId === "geral-acao" && (
              <RelatorioGeralAcao selectedReportLabel={`${selectedReport.order} ${selectedReport.title}`} />
            )}
            {selectedReportId === "total-participacoes" && (
              <RelatorioTotalParticipacoes selectedReportLabel={`${selectedReport.order} ${selectedReport.title}`} />
            )}
            {selectedReportId === "consulta-participantes" && (
              <RelatorioConsultaParticipantes selectedReportLabel={`${selectedReport.order} ${selectedReport.title}`} />
            )}
            {selectedReportId === "inscritos-municipio" && (
              <RelatorioInscritosPorMunicipio selectedReportLabel={`${selectedReport.order} ${selectedReport.title}`} />
            )}
            {selectedReportId === "total-qualificados" && (
              <RelatorioTotalQualificados selectedReportLabel={`${selectedReport.order} ${selectedReport.title}`} />
            )}
            {selectedReportId === "migracao-educasuas" && (
              <RelatorioMigracaoEducaSuas selectedReportLabel={`${selectedReport.order} ${selectedReport.title}`} />
            )}
            {selectedReportId === "relatorio-gerencial" && (
              <RelatorioGerencialCensoSuas selectedReportLabel={`${selectedReport.order} ${selectedReport.title}`} />
            )}
          </section>
        </div>
      </CardContent>
    </Card>
  );
};

export default RelatoriosManager;
